/**
 * CSFixationFormDialog — Create / Edit CS Fixation (chốt giá NPE).
 * Fields:
 *   - consignment_no (autocomplete consignment_p)
 *   - supplier_name (display)
 *   - quality_code (display)
 *   - kg (input)
 *   - price (input)
 *   - fixation_date
 *   - p_contract_no (autocomplete LocalP)
 *   - note
 */
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { useSnackbar } from '@/hooks/useSnackbar'
import { useListAllContractsQuery } from '@/store/api/contractsAllApi'
import {
  useCreateCSFixationMutation,
  useUpdateCSFixationMutation,
} from '@/store/api/csFixationsApi'
import { useListLocalPQuery } from '@/store/api/localPApi'
import type { CSFixationCreateIn, CSFixationOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

export interface CSFixationFormDialogProps {
  open: boolean
  mode: 'create' | 'edit'
  fixedNpeContractNo?: string
  initialValue?: CSFixationOut | null
  onClose: () => void
  onSaved?: (id: number) => void
}

interface CSFixFormValues {
  contract_no: string // NPE
  fixation_date: string
  quantity_kg: number | ''
  price: number | ''
  p_contract_no: string
  note: string
}

const EMPTY: CSFixFormValues = {
  contract_no: '',
  fixation_date: dayjs().format('YYYY-MM-DD'),
  quantity_kg: '',
  price: '',
  p_contract_no: '',
  note: '',
}

const schema: yup.ObjectSchema<CSFixFormValues> = yup.object({
  contract_no: yup.string().trim().required('Bắt buộc chọn HĐ NPE'),
  fixation_date: yup.string().required('Bắt buộc chọn ngày'),
  quantity_kg: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập SL', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'SL phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  price: yup
    .mixed<number | ''>()
    .test('required', 'Bắt buộc nhập giá', (v) => v !== '' && v !== undefined && v !== null)
    .test('positive', 'Giá phải > 0', (v) => typeof v === 'number' && v > 0)
    .required(),
  p_contract_no: yup.string().trim().default(''),
  note: yup.string().trim().default(''),
}) as yup.ObjectSchema<CSFixFormValues>

function fromCSFix(c: CSFixationOut): CSFixFormValues {
  return {
    contract_no: c.contract_no ?? '',
    fixation_date: c.fixation_date ?? '',
    quantity_kg: c.quantity_kg ?? '',
    price: c.price ?? '',
    p_contract_no: c.p_contract_no ?? '',
    note: c.note ?? '',
  }
}

function toApi(v: CSFixFormValues): CSFixationCreateIn {
  return {
    contract_no: v.contract_no.trim(),
    fixation_date: v.fixation_date,
    quantity_kg: typeof v.quantity_kg === 'number' ? v.quantity_kg : 0,
    price: typeof v.price === 'number' ? v.price : 0,
    p_contract_no: v.p_contract_no.trim() || null,
    note: v.note.trim() || null,
  }
}

export function CSFixationFormDialog({
  open,
  mode,
  fixedNpeContractNo,
  initialValue,
  onClose,
  onSaved,
}: CSFixationFormDialogProps) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createCSFix, { isLoading: creating }] = useCreateCSFixationMutation()
  const [updateCSFix, { isLoading: updating }] = useUpdateCSFixationMutation()
  const loading = creating || updating

  // Autocomplete: union contracts (filter NPE only on FE side via prefix)
  const { data: contractsRes } = useListAllContractsQuery({
    page: 1,
    page_size: 500,
  })
  const npeOptions = useMemo(
    () =>
      (contractsRes?.items ?? []).filter(
        (c) => c.source === 'npe' || c.contract_no?.startsWith('NPE-'),
      ),
    [contractsRes],
  )

  // LocalP for p_contract_no autocomplete
  const { data: localPList } = useListLocalPQuery({
    page: 1,
    page_size: 500,
    is_status: 1,
  })
  const localPOptions = useMemo(() => localPList?.items ?? [], [localPList])

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<CSFixFormValues>({
    resolver: yupResolver(schema),
    defaultValues: EMPTY,
  })

  const selectedNpe = watch('contract_no')

  // Lookup contract info for display (supplier, quality)
  const npeInfo = useMemo(() => {
    return contractsRes?.items?.find((c) => c.contract_no === selectedNpe) ?? null
  }, [contractsRes, selectedNpe])

  const [supplierDisplay, setSupplierDisplay] = useState('')
  const [qualityDisplay, setQualityDisplay] = useState('')
  useEffect(() => {
    if (initialValue) {
      setSupplierDisplay(initialValue.supplier_name ?? '')
      setQualityDisplay(initialValue.quality_code ?? '')
    } else {
      setSupplierDisplay(npeInfo?.short_name ?? '')
      setQualityDisplay('')
    }
  }, [initialValue, npeInfo])

  useEffect(() => {
    if (!open) return
    if (isEdit && initialValue) {
      reset(fromCSFix(initialValue))
    } else {
      reset({ ...EMPTY, contract_no: fixedNpeContractNo ?? '' })
    }
  }, [open, isEdit, initialValue, fixedNpeContractNo, reset])

  const onSubmit = handleSubmit(
    async (values) => {
      const payload = toApi(values)
      try {
        if (isEdit && initialValue) {
          const res = await updateCSFix({
            id: initialValue.cs_fixation_id,
            body: payload,
          }).unwrap()
          snack.success('Đã cập nhật chốt giá')
          onSaved?.(res.data?.cs_fixation_id ?? initialValue.cs_fixation_id)
        } else {
          const res = await createCSFix(payload).unwrap()
          snack.success('Đã tạo chốt giá')
          onSaved?.(res.data?.cs_fixation_id ?? 0)
        }
        onClose()
      } catch (err) {
        snack.error(
          getErrorMessage(err, isEdit ? 'Cập nhật thất bại' : 'Tạo chốt giá thất bại'),
        )
      }
    },
    () => snack.error('Vui lòng kiểm tra lại các trường còn lỗi'),
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEdit ? 'Sửa chốt giá NPE' : 'Tạo chốt giá NPE'}</DialogTitle>
      <DialogContent dividers>
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <Box display="flex" gap={2} flexWrap="wrap">
            {fixedNpeContractNo ? (
              <TextField
                label="Hợp đồng NPE"
                size="small"
                value={fixedNpeContractNo}
                disabled
                sx={{ flex: '2 1 280px' }}
              />
            ) : (
              <Controller
                control={control}
                name="contract_no"
                render={({ field }) => {
                  const found = npeOptions.find((c) => c.contract_no === field.value)
                  const selected =
                    found ??
                    (field.value
                      ? ({
                          contract_no: field.value,
                          short_name: null,
                          cp_code: null,
                          contract_date: null,
                          license_cert_no: null,
                          certification: null,
                          source: 'npe',
                        } as (typeof npeOptions)[number])
                      : null)
                  return (
                    <Autocomplete
                      size="small"
                      sx={{ flex: '2 1 280px' }}
                      options={npeOptions}
                      value={selected}
                      onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                      getOptionLabel={(opt) =>
                        opt
                          ? `${opt.contract_no} — ${opt.short_name ?? opt.cp_code ?? ''}`
                          : ''
                      }
                      isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                      disabled={isEdit}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Hợp đồng NPE *"
                          error={!!errors.contract_no}
                          helperText={errors.contract_no?.message}
                        />
                      )}
                    />
                  )
                }}
              />
            )}
            <TextField
              label="Số chốt giá"
              size="small"
              value={initialValue?.fixation_no ?? '— Tự sinh —'}
              disabled
              sx={{ flex: '1 1 180px' }}
            />
            <Controller
              control={control}
              name="fixation_date"
              render={({ field }) => (
                <DatePicker
                  label="Ngày chốt giá *"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { flex: '1 1 180px' },
                      error: !!errors.fixation_date,
                      helperText: errors.fixation_date?.message,
                    },
                  }}
                />
              )}
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <TextField
              label="Nhà cung cấp"
              size="small"
              value={supplierDisplay}
              disabled
              sx={{ flex: '2 1 280px' }}
            />
            <TextField
              label="Quality code"
              size="small"
              value={qualityDisplay}
              disabled
              sx={{ flex: '1 1 180px' }}
            />
          </Box>

          <Box display="flex" gap={2} flexWrap="wrap">
            <Controller
              control={control}
              name="quantity_kg"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Số lượng (kg) *"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.quantity_kg}
                  helperText={errors.quantity_kg?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="price"
              render={({ field }) => (
                <TextField
                  {...field}
                  value={field.value === '' ? '' : field.value}
                  onChange={(e) =>
                    field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                  }
                  label="Giá *"
                  type="number"
                  size="small"
                  sx={{ flex: '1 1 180px' }}
                  error={!!errors.price}
                  helperText={errors.price?.message}
                />
              )}
            />
          </Box>

          <Controller
            control={control}
            name="p_contract_no"
            render={({ field }) => {
              const selected =
                localPOptions.find((c) => c.contract_no === field.value) ??
                (field.value
                  ? ({
                      contract_no: field.value,
                      supplier_name: null,
                      cp_code: null,
                    } as (typeof localPOptions)[number])
                  : null)
              return (
                <Autocomplete
                  size="small"
                  options={localPOptions}
                  value={selected}
                  onChange={(_e, v) => field.onChange(v?.contract_no ?? '')}
                  getOptionLabel={(opt) =>
                    opt
                      ? `${opt.contract_no} — ${opt.supplier_name ?? opt.cp_code ?? ''}`
                      : ''
                  }
                  isOptionEqualToValue={(o, v) => o.contract_no === v.contract_no}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Local P contract (sinh ra)"
                      helperText={
                        errors.p_contract_no?.message ??
                        'Để trống nếu BE tự sinh khi commit allocation'
                      }
                    />
                  )}
                />
              )
            }}
          />

          <Controller
            control={control}
            name="note"
            render={({ field }) => (
              <TextField
                {...field}
                label="Ghi chú"
                size="small"
                multiline
                minRows={2}
                fullWidth
                error={!!errors.note}
                helperText={errors.note?.message}
              />
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Huỷ
        </Button>
        <Button
          onClick={() => void onSubmit()}
          variant="contained"
          color="primary"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CSFixationFormDialog
